// utils/tokens/index.ts
import { format, startOfWeek, startOfMonth } from "date-fns";
import { getDocById, updateDocById } from "../firebase";
import { CalooAdminConfig } from "../admin";
import { logEvent } from "@/utils/logs";
import appStore from "@/utils/store";
import { CalooPremium } from "../premium";
export interface TokenCounts {
    daily: number;
    weekly: number;
    monthly: number;
    lastUpdated: string; // YYYY-MM-DD
    weekStart: string;   // YYYY-MM-DD
    monthStart: string;  // YYYY-MM-DD
    tokens: number; // total openAI tokens used
    tokensText: number; // voice/text openAI tokens used
}

let counts: TokenCounts | null = {
    daily: 0,
    weekly: 0,
    monthly: 0,
    lastUpdated: getToday(),
    weekStart: getWeekStart(),
    monthStart: getMonthStart(),
    tokens: 0, // total openAI tokens used
    tokensText: 0, // voice/text openAI tokens used
};

export function getToday(): string {
    return format(new Date(), "yyyy-MM-dd");
}

export function getWeekStart(): string {
    return format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd");
}

export function getMonthStart(): string {
    return format(startOfMonth(new Date()), "yyyy-MM-dd");
}

/**
 * Load counts from Firestore USERS collection
 */
export function loadTokenCounts(remote: any): TokenCounts {

    if (remote) {
        counts = remote as TokenCounts;
    }

    if (!counts) {
        counts = {
            daily: 0,
            weekly: 0,
            monthly: 0,
            lastUpdated: getToday(),
            weekStart: getWeekStart(),
            monthStart: getMonthStart(),
            tokens: 0, // total openAI tokens used
            tokensText: 0, // voice/text openAI tokens used
        };
    } else {
        resetIfNeeded();
    }

    return counts;
}

/**
 * Increment counts with text tokens and save
 */
export async function incrementTokensText(userId: string, tokens: number) {
    if (!counts) throw new Error("Counts not loaded");
    resetIfNeeded();
    counts.tokensText += tokens;

    appStore.set("user", { ...appStore.get("user"), tokens: counts });
    await updateDocById("users", userId, {
        tokens: counts,
    });
}

/**
 * Increment counts and save
 */
export async function incrementTokens(userId: string) {
    if (!counts) throw new Error("Counts not loaded");

    resetIfNeeded();

    counts.daily++;
    counts.weekly++;
    counts.monthly++;

    appStore.set("user", { ...appStore.get("user"), tokens: counts });
    await updateDocById("users", userId, {
        tokens: counts,
    });

}

/**
 * Check if limits are exceeded
 */
export function isLimitExceeded(): 'day' | 'week' | 'month' | null {
    if (!counts) throw new Error("Counts not loaded");

    const limits = CalooPremium.isPremiumTrial ? getTokenLimitsFriends() : getTokenLimits();
    console.log("Token limits:", limits);

    if (counts.daily >= limits.daily) return 'day';
    if (counts.weekly >= limits.weekly) return 'week';
    if (counts.monthly >= limits.monthly) return 'month';

    return null;
}

/**
 * Check if limits tokens are exceeded
 */
export function isLimiTokensExceeded(): boolean {
    if (!counts) throw new Error("Counts not loaded");

    const limits = CalooPremium.isPremiumTrial ? getTokenLimitsFriends() : getTokenLimits();
    console.log("Token limits:", limits);
    return (
        counts.tokensText >= limits.tokensText
    );
}

/**
 * Get current counts
 */
export function getCounts(): TokenCounts {
    if (!counts) throw new Error("Counts not loaded");
    return counts;
}

/**
 * Reset daily, weekly, monthly counters if needed
 */
function resetIfNeeded() {
    const today = getToday();
    const week = getWeekStart();
    const month = getMonthStart();

    if (counts!.lastUpdated !== today) {
        counts!.daily = 0;
        counts!.lastUpdated = today;
        counts!.tokens = 0; // Reset total tokens used
        counts!.tokensText = 0; // Reset voice/text tokens used
    }
    if (counts!.weekStart !== week) {
        counts!.weekly = 0;
        counts!.weekStart = week;
    }
    if (counts!.monthStart !== month) {
        counts!.monthly = 0;
        counts!.monthStart = month;
    }
}

/**
 * Helper to read limits from CalooAdminConfig
 */
function getTokenLimits(): { daily: number; weekly: number; monthly: number; tokensText: number } {
    if (
        !CalooAdminConfig ||
        !CalooAdminConfig.nijiConfig ||
        !CalooAdminConfig.nijiConfig.tokenLimits
    ) {
        logEvent("Token limits config not found");
        return {
            daily: 30,
            weekly: 120,
            monthly: 500,
            tokensText: 25000, // voice/text openAI tokens used
        };
    }

    return CalooAdminConfig.nijiConfig.tokenLimits;
}
function getTokenLimitsFriends(): { daily: number; weekly: number; monthly: number; tokensText: number } {
    if (
        !CalooAdminConfig ||
        !CalooAdminConfig.nijiConfig ||
        !CalooAdminConfig.nijiConfig.tokenLimitsFriends
    ) {
        logEvent("Token limits Friends config not found");
        return {
            daily: 10,
            weekly: 70,
            monthly: 250,
            tokensText: 5000, // voice/text openAI tokens used
        };
    }
    return CalooAdminConfig.nijiConfig.tokenLimitsFriends;
}